import Splide from '@splidejs/splide';

const CAROUSEL_CONFIG = {
  arrows: false,
  classes: {
    page: 'splide__pagination__page product-gallery__page',
    pagination: 'splide__pagination product-gallery__pagination',
  },
  pagination: true,
  rewind: true,
  speed: 800,
  type: 'fade',
};

const MOBILE_MQ = '(max-width: 1023px)';

class LyleProductGallery extends HTMLElement {
  #carousel = null;
  #mobileMql = null;
  #mqlHandler = null;
  #clickHandler = null;
  #keyHandler = null;
  #variantHandler = null;
  #openHandler = null;
  #closeHandler = null;
  #observer = null;
  #pinchZoom = null;
  #pinchZoomPromise = null;
  #pinchActive = false;

  connectedCallback() {
    this.#mobileMql = window.matchMedia(MOBILE_MQ);

    this.#mqlHandler = (e) => {
      if (e.matches) this.#mountCarousel();
      else this.#destroyCarousel();
      this.#syncZoomLists();
    };
    this.#mobileMql.addEventListener('change', this.#mqlHandler);
    if (this.#mobileMql.matches) this.#mountCarousel();
    this.#syncZoomLists();

    this.#clickHandler = (e) => {
      if (e.target.closest('[data-product-gallery-close]')) {
        this.#closeDialog();
        return;
      }

      const pinchTrigger = e.target.closest('[data-zoom-pinch-src]');
      if (pinchTrigger && this.#mobileMql.matches) {
        e.preventDefault();
        this.#openPinchZoom(pinchTrigger.dataset.zoomPinchSrc);
        return;
      }

      const trigger = e.target.closest('[data-gallery-trigger], [data-slide-index]');
      if (trigger) {
        this.#openDialog(trigger.dataset.mediaId);
      }
    };
    this.addEventListener('click', this.#clickHandler);

    this.#keyHandler = (e) => {
      if (e.key === 'Escape') {
        if (this.#pinchActive) {
          this.#destroyPinchZoom();
          return;
        }
        this.#closeDialog();
      }
    };
    this.addEventListener('keydown', this.#keyHandler);

    this.#variantHandler = (e) => {
      const { mediaId } = e.detail;
      if (mediaId) this.#syncVariant(String(mediaId));
    };
    document.addEventListener('product-gallery:variant', this.#variantHandler);

    this.#openHandler = (e) => {
      this.#openDialog(e.detail?.mediaId);
    };
    document.addEventListener('product-gallery:open', this.#openHandler);

    const dialog = this.querySelector('[data-product-gallery-dialog]');
    this.#closeHandler = () => {
      this.#destroyPinchZoom();
      this.#teardownZoomObserver();
    };
    dialog?.addEventListener('close', this.#closeHandler);

    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) this.#syncVariant(initialMediaId);
  }

  disconnectedCallback() {
    this.#destroyCarousel();
    this.#destroyPinchZoom();
    this.#teardownZoomObserver();

    if (this.#mobileMql && this.#mqlHandler) {
      this.#mobileMql.removeEventListener('change', this.#mqlHandler);
    }
    this.#mobileMql = null;
    this.#mqlHandler = null;

    if (this.#clickHandler) {
      this.removeEventListener('click', this.#clickHandler);
      this.#clickHandler = null;
    }
    if (this.#keyHandler) {
      this.removeEventListener('keydown', this.#keyHandler);
      this.#keyHandler = null;
    }
    if (this.#variantHandler) {
      document.removeEventListener('product-gallery:variant', this.#variantHandler);
      this.#variantHandler = null;
    }
    if (this.#openHandler) {
      document.removeEventListener('product-gallery:open', this.#openHandler);
      this.#openHandler = null;
    }

    const dialog = this.querySelector('[data-product-gallery-dialog]');
    if (dialog && this.#closeHandler) {
      dialog.removeEventListener('close', this.#closeHandler);
    }
    this.#closeHandler = null;
  }

  #mountCarousel() {
    if (this.#carousel) return;
    const el = this.querySelector('[data-product-gallery-carousel]');
    if (!el) return;
    const list = el.querySelector('.splide__list');
    if (!list || list.children.length === 0) return;

    const count = list.children.length;
    this.#carousel = new Splide(el, {
      ...CAROUSEL_CONFIG,
      drag: count > 1,
      pagination: count > 1,
      rewind: count > 1,
    });
    this.#carousel.mount();
  }

  #destroyCarousel() {
    if (!this.#carousel) return;
    this.#carousel.destroy();
    this.#carousel = null;
  }

  #activeZoomList() {
    const mobileList = this.querySelector('[data-zoom-list="mobile"]');
    if (mobileList && this.#mobileMql?.matches) return mobileList;
    return this.querySelector('[data-zoom-list="desktop"]');
  }

  #syncZoomLists() {
    const desktopList = this.querySelector('[data-zoom-list="desktop"]');
    const mobileList = this.querySelector('[data-zoom-list="mobile"]');
    if (!mobileList) {
      desktopList?.removeAttribute('hidden');
      return;
    }

    if (this.#mobileMql?.matches) {
      desktopList?.setAttribute('hidden', '');
      mobileList.removeAttribute('hidden');
    } else {
      mobileList.setAttribute('hidden', '');
      desktopList?.removeAttribute('hidden');
    }
  }

  #openDialog(mediaId) {
    const dialog = this.querySelector('[data-product-gallery-dialog]');
    if (!dialog) return;

    this.#syncZoomLists();
    dialog.showModal();
    this.#setupZoomObserver();
    this.#scrollToZoomImage(mediaId);
  }

  #closeDialog() {
    this.#destroyPinchZoom();
    const dialog = this.querySelector('[data-product-gallery-dialog]');
    dialog?.close();
  }

  #scrollToZoomImage(mediaId) {
    const list = this.#activeZoomList();
    if (!list) return;

    let target = null;
    if (mediaId) {
      target = list.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    }
    if (!target) target = list.querySelector('[data-zoom-observed]');
    if (!target) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
      }, 50);
    });
  }

  #setupZoomObserver() {
    this.#teardownZoomObserver();
    const list = this.#activeZoomList();
    if (!list) return;

    const items = list.querySelectorAll('[data-zoom-observed]');
    if (items.length === 0) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const mediaId = entry.target.dataset.mediaId;
          if (!mediaId) continue;
          this.#syncVariant(mediaId);
        }
      },
      { root: null, rootMargin: '-50% 0% -50% 0%', threshold: 0 }
    );

    for (const item of items) {
      this.#observer.observe(item);
    }
  }

  #teardownZoomObserver() {
    if (!this.#observer) return;
    this.#observer.disconnect();
    this.#observer = null;
  }

  async #openPinchZoom(src) {
    if (this.#pinchActive || !src) return;

    if (!this.#pinchZoomPromise) {
      this.#pinchZoomPromise = import('pinch-zoom-js');
    }

    const { default: PinchZoom } = await this.#pinchZoomPromise;
    if (!this.isConnected || this.#pinchActive) return;

    const viewport = this.querySelector('[data-pinch-viewport]');
    const image = this.querySelector('[data-pinch-image]');
    if (!viewport || !image) return;

    image.src = src;
    viewport.hidden = false;
    this.#pinchActive = true;
    this.classList.add('is-pinch-active');

    this.#pinchZoom = new PinchZoom(image, {
      animationDuration: 50,
      minZoom: 1,
      setOffsetsOnce: true,
      use2d: false,
      onDoubleTap: () => {
        this.#destroyPinchZoom();
      },
    });
  }

  #destroyPinchZoom() {
    const viewport = this.querySelector('[data-pinch-viewport]');
    let image = this.querySelector('[data-pinch-image]');

    if (this.#pinchZoom) {
      const el = this.#pinchZoom.el;
      const container = this.#pinchZoom.container;
      if (container?.parentNode && el) {
        container.parentNode.insertBefore(el, container);
      }
      this.#pinchZoom.destroy();
      this.#pinchZoom = null;
      image = el || image;
    }

    this.#pinchActive = false;
    this.classList.remove('is-pinch-active');
    if (viewport) viewport.hidden = true;
    if (image) image.removeAttribute('src');
  }

  #syncVariant(mediaId) {
    if (this.#carousel) {
      const slides = this.querySelectorAll('[data-product-gallery-carousel] [data-media-id]');
      let targetIndex = -1;
      slides.forEach((slide, i) => {
        if (slide.dataset.mediaId === mediaId) targetIndex = i;
      });
      if (targetIndex >= 0) this.#carousel.go(targetIndex);
      return;
    }

    const gridImage = this.querySelector(
      `[data-product-gallery-grid] [data-media-id="${CSS.escape(mediaId)}"]`
    );
    gridImage?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (!customElements.get('lyle-product-gallery')) {
  customElements.define('lyle-product-gallery', LyleProductGallery);
}
